// date related //
/**
 * Date formats listed below:
 * semanticDate: "Oct 12, 2021"
 * semanticDateTime: "Oct 12, 2021 12:00 PM"
 * fullDateTime: "2024-05-14 00:00" || "YYYY-MM-DD HH:MM"
 * TODO: to be continued...
 *
 */

// byte formatting //

/**
 * Returns semantic byte size of given amount of bytes where
 * 1 GB = 1000 MB
 * @param {number} bytes
 * @returns {string} "X.XX GB or X.XX MB or X.XX KB or X.XX B"
 */
export function semanticBytes(bytes) {
  if (parseFloat(bytes) == NaN) return "0 B";
  if (bytes === 0) return "0 B";
  const k = 1000;
  const dm = 2;
  const sizes = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
}

// other//

/**
 * Copies given text to clipboard, shows a toast message by default
 *
 * @param {String} text
 * @param {String} toastMessage - Default is "Copied to clipboard"
 * @param {boolean} showToast - Default is true
 */
export function copyToClipboard(
  text,
  vueInstance,
  toastMessage = "Copied to clipboard"
) {
  const copyRequest = navigator.clipboard.writeText(text);
  if (
    toastMessage !== "" &&
    toastMessage !== null &&
    vueInstance != undefined
  ) {
    vueInstance.sixToast({
      message: toastMessage,
      type: "success",
      title: "Success",
      timeout: 2000,
    });
  }
  return copyRequest;
}

/**
 * Returns semantic asset type of given asset type
 * @param {String} assetType
 * @returns {String} "Pico LTE" || "SuperSIM" ||
 *                   "CCC" || "CORE Device + SuperSIM" || "ALPON X4" ||
 *                   "ALPON X5 AI" || "ProSIM" || "Pro eSIM" || "Super eSIM"
 */

export function semanticAssetType(assetType) {
  let typeTexts = {
    picolte: "Pico LTE",
    plastic_sim: "SuperSIM",
    core: "CORE Device + SuperSIM",
    alpon_x4: "ALPON X4",
    alpon_x5_ai: "ALPON X5 AI",
    axis_gw: "Axis GW",
    stellar_sim: "ProSIM",
    eprosim: "Pro eSIM",
    esupersim: "Super eSIM",
  };

  return typeTexts[assetType] || assetType;
}

/**
 * Returns the eSIM activation code in the "LPA:1$<SM-DP+>$<matching id>"
 * format devices expect in QR codes. KORE returns the code without the
 * "LPA:" prefix.
 * @param {String} activationCode
 * @returns {String}
 */
export function lpaActivationCode(activationCode) {
  if (!activationCode || /^LPA:/i.test(activationCode)) {
    return activationCode;
  }
  return `LPA:${activationCode}`;
}

/**
 * Formats activity log text based on metadata and detail.
 * @param {Object} activity - The activity log object.
 * @returns {String} - Formatted activity log text.
 */
export function formatLogText(activity) {
  const {metadata = {}, detail = {}} = activity || {};
  let text = "";

  switch (metadata.category) {
    case "datapools":
      if (metadata.action === "subscribe") {
        text = `Subscribed to package: ${
          detail.package_name
        } - ${semanticBytes(detail.data_volume)}`;
      } else if (metadata.action === "unsubscribe") {
        text = `Unsubscribed from package: ${
          detail.package_name
        } - ${semanticBytes(detail.data_volume)}`;
      } else {
        text = `Performed action ${metadata.category}.${metadata.action}`;
      }
      break;
    case "network_groups":
      if (metadata.action === "create") {
      text = `Created network group: ${detail.network_group_name}`;
      } else if (metadata.action === "update") {
      text = `Updated network group: ${detail.network_group_name}`;
      } else if (metadata.action === "delete") {
      text = `Deleted network group: ${detail.network_group_name}`;
      } else {
        text = `Performed action ${metadata.category}.${metadata.action}`;
      }
      break;
    case "billing":
      if (metadata.action === "setup_intent") {
        text = formatSetupIntentText(detail);
      } else if (metadata.action === "delete_payment_method") {
        text = `Deleted a payment method`;
      } else if (metadata.action === "update_preferences") {
        text = `Updated billing preferences`;
      } else if (metadata.action === "add_credits") {
        text = `Added credits: $${detail.amount} from ${detail.source}`;
      } else if (metadata.action === "refund_credits") {
        text = `Received refund: $${detail.amount} from ${detail.source}`;
      } else if (metadata.action === "buy_esim") {
        text = `Purchased ${detail.quantity} ${semanticAssetType(detail.product)} for $${Number(detail.total_price).toFixed(2)}`;
      } else {
        text = `Performed action ${metadata.category}.${metadata.action}`;
      }
      break;
    case "assets":
      if (metadata.action === "register") {
        text = `Registered asset: ${detail.asset_name}`;
      } else if (metadata.action === "activate_sim") {
        text = `Activated asset: ${detail.asset_name}`;
      } else if (metadata.action === "inactivate_sim") {
        text = `Deactivated asset: ${detail.asset_name}`;
      } else if (metadata.action === "update_network_group") {
        text = `Updated network group of asset: ${detail.asset_name}`;
      } else if (metadata.action === "update_sim_network_group"){
        text = `Updated SIM network group of asset: ${detail.asset_name}`;
      }
      else if (metadata.action === "open_terminal") {
        text = `Opened remote terminal for asset: ${detail.asset_name}`;
      } else if (metadata.action === "alpon_download_esim_profile"){
        text = `Downloaded eSIM profile for asset: ${detail.asset_name}`;
      } else if (metadata.action === "alpon_switch_esim_profile") {
        text = `Switched eSIM profile for asset: ${detail.asset_name}`;
      } else if (metadata.action === "update_name") {
        const oldName = detail.old_name || detail.asset_name;
        text = `Updated asset name: ${oldName} to ${detail.new_name}`;
      } else if (metadata.action === "update_status") {
        text = `Updated asset status to ${detail.new_status}`;
      } else if (metadata.action === "alpon_deploy_container") {
        text = `Deployed container ${detail.container_name} to asset: ${detail.asset_name}`;
      } else if (metadata.action === "alpon_delete_container") {
        text = `Deleted container ${detail.container_name} from asset: ${detail.asset_name}`;
      } else if (metadata.action === "alpon_update_container") {
        text = `Updated container ${detail.container_name} on asset: ${detail.asset_name}`;
      } else if (metadata.action === "alpon_set_network_priorities") {
        text = `Set network priorities on asset: ${detail.asset_name}`;
      } else if (metadata.action === "alpon_restart_container") {
        text = `Restarted container ${detail.container_name} on asset: ${detail.asset_name}`;
      } else if (metadata.action === "alpon_pause_container") {
        text = `Paused container ${detail.container_name} on asset: ${detail.asset_name}`;
      } else if (metadata.action === "alpon_resume_container") {
        text = `Resumed container ${detail.container_name} on asset: ${detail.asset_name}`;
      } else if (metadata.action === "alpon_connect_wifi") {
        text = `Connected asset ${detail.asset_name} to Wi-Fi network ${detail.ssid}`;
      } else if (metadata.action === "alpon_enable_remote_support") {
        text = `Enabled remote support on asset: ${detail.asset_name}`;
      } else if (metadata.action === "alpon_disable_remote_support") {
        text = `Disabled remote support on asset: ${detail.asset_name}`;
      } else if (metadata.action === "alpon_reboot_device") {
        text = `Rebooted asset: ${detail.asset_name}`;
      } else if (metadata.action === "transfer_confirmed") {
        text = `Confirmed asset transfer`;
      } else if (metadata.action === "transfer_rejected") {
        text = `Rejected asset transfer`;
      } else {
        text = `Performed action ${metadata.category}.${metadata.action}`;
      }
      break;
    case "tags":
      if (metadata.action === "create") {
        text = `Created tag: ${detail.tag_name}`;
      } else if (metadata.action === "update") {
        text = `Updated tag: ${detail.tag_name}`;
      } else if (metadata.action === "delete") {
        text = `Deleted tag: ${detail.tag_name}`;
      } else {
        text = `Performed action ${metadata.category}.${metadata.action}`;
      }
      break;
    case "core":
      if (metadata.action === "create_bulk_deployment") {
        text = `Created bulk deployment: ${detail.deployment_name}`;
      } else if (metadata.action === "update_bulk_deployment") {
        text = `Updated bulk deployment: ${detail.deployment_name}`;
      } else {
        text = `Performed action ${metadata.category}.${metadata.action}`;
      }
      break;
    case "developers":
      if (metadata.action === "create_api_key") {
        text = `Created API Key: ${detail.api_key_name}`;
      } else if (metadata.action === "delete_api_key") {
        text = `Deleted API Key: ${detail.api_key_name}`;
      } else {
        text = `Performed action ${metadata.category}.${metadata.action}`;
      }
      break;
    case "auth":
      if (metadata.action === "login") {
        text = `Logged in`;
      } else if (metadata.action === "logout") {
        text = `Logged out`;
      } else if (metadata.action === "switch_account") {
        text = `A team member accessed to your account`;
      } else {
        text = `Performed action ${metadata.category}.${metadata.action}`;
      }
      break;
    case "teams":
      if (metadata.action === "add_member") {
        text = `Added team member: ${detail.added_member}`;
      } else if (metadata.action === "remove_member") {
        text = `Removed team member: ${detail.removed_member}`;
      } else if (metadata.action === "update_member") {
        text = `Updated team member: ${detail.updated_member}`;
      } else {
        text = `Performed action ${metadata.category}.${metadata.action}`;
      }
      break;
    case "reports":
      if (metadata.action === "generate") {
        text = `Generated report: ${detail.report_name} for ${detail.report_date}`;
      } else if (metadata.action === "download") {
        text = `Downloaded report: ${detail.report_name} for ${detail.report_date}`;
      } else {
        text = `Performed action ${metadata.category}.${metadata.action}`;
      }
      break;
    case "alpon_fleets":
      if (metadata.action === "created") {
        text = `Created ALPON fleet "${detail.fleet_name}"`;
      } else if (metadata.action === "pilot_changed") {
        text = `Changed pilot of fleet "${detail.fleet_name}" to "${detail.new_pilot_asset_name}"`;
      } else if (metadata.action === "assets_added") {
        text = `Added ${detail.assets_added.length} asset(s) to fleet "${detail.fleet_name}"`;
      } else if (metadata.action === "assets_deleted") {
        text = `Removed ${detail.assets_deleted.length} asset(s) from fleet "${detail.fleet_name}"`;
      } else if (metadata.action === "deleted") {
        text = `Deleted ALPON fleet "${detail.fleet_name}"`;
      } else if (metadata.action === "rollout") {
        text = `Released deployment "${detail.version}" for fleet "${detail.fleet_name}"`;
      } else if (metadata.action === "reboot") {
        text = `Requested reboot for ${detail.asset_count} devices in fleet "${detail.fleet_name}"`;
      } else if (metadata.action === "pilot_maintenance_started") {
        text = `Started pilot update to "${detail.target_bundle_version}" for fleet "${detail.fleet_name}"`;
      } else if (metadata.action === "pilot_app_deploy_started") {
        text = `Started deploying container "${detail.app_name}" to pilot of fleet "${detail.fleet_name}"`;
      } else if (metadata.action === "pilot_app_update_started") {
        text = `Started updating container "${detail.app_name}" on pilot of fleet "${detail.fleet_name}"`;
      } else if (metadata.action === "pilot_app_deleted") {
        text = `Deleted container "${detail.app_name}" from pilot of fleet "${detail.fleet_name}"`;
      } else if (metadata.action === "deployment_revert_started") {
        text = `Started reverting fleet "${detail.fleet_name}" to deployment "${detail.source_version}"`;
      } else {
        text = `Performed action ${metadata.category}.${metadata.action}`;
      }
      break;
    case "registry":
      if (metadata.action === "create_user") {
        text = `Created registry user: ${detail.registry_user_id}`;
      } else if (metadata.action === "reset_password") {
        text = "Reset registry password";
      } else if (metadata.action === "delete_image") {
        text = `Deleted registry image: ${detail.image_name}`;
      } else if (metadata.action === "delete_tag") {
        text = `Deleted registry tag: ${detail.image_name}:${detail.tag_name}`;
      } else {
        text = `Performed action ${metadata.category}.${metadata.action}`;
      }
      break;

    default:
      text = `Performed action ${metadata.category}.${metadata.action}`;
  }

  return text;
}

/**
 * Helper function to format setup intent text based on status.
 * @param {Object} detail - The detail object containing setup intent status.
 * @returns {String} - Formatted setup intent text.
 */
export function formatSetupIntentText(detail) {
  let text = "Setup intent";

  switch (detail.setup_intent_status) {
    case "requires_payment_method":
      text += " requires a payment method.";
      break;
    case "requires_confirmation":
      text += " requires confirmation.";
      break;
    case "requires_action":
      text += " requires additional action.";
      break;
    case "processing":
      text += " is processing.";
      break;
    case "requires_capture":
      text += " requires capture.";
      break;
    case "canceled":
      text += " was canceled.";
      break;
    case "succeeded":
      text += ` succeeded with payment method ${detail.payment_method}.`;
      break;
    default:
      text += ` has an unknown status: ${detail.setup_intent_status}.`;
  }

  return text;
}
